/**
 * Accordion wrapper panel
 */

import React from 'react'

import CaretDownIcon from '@ant-design/icons/CaretDownFilled'
import CaretRightIcon from '@ant-design/icons/CaretRightFilled'
import Accordion from '@material-ui/core/Accordion'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import Chip from '@material-ui/core/Chip'
import Slide from '@material-ui/core/Slide'
import { withStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import LockIcon from '@mui/icons-material/LockOpen'
import isNil from 'lodash/isNil'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import * as PoziUtils from 'api/utils'
import { ICON_COLOR_DISABLED, ICON_COLOR_ENABLED } from 'drawer/tabs/layers/panels/LayerPanels'

const styles = theme => ({
  root: {
    backgroundColor: 'transparent',
    width: '100%'
  },
  titleNormal: {
    fontWeight: 'normal',
    fontSize: 10
  },
  titleBold: {
    fontWeight: 'bold !important', // hack to override default specificity
    fontSize: 10
  },
  title: {
    ...theme.typography.overline,
    flex: 1,
    fontSize: 10,
    lineHeight: 1.4,
    paddingTop: 5,
    paddingLeft: 7,
    color: '#303030'
  },
  titleNotLicensed: {
    ...theme.typography.overline,
    flex: 1,
    fontSize: 10,
    lineHeight: 1.4,
    paddingTop: 5,
    paddingLeft: 12,
    color: ICON_COLOR_DISABLED
  },
  caretIcon: {
    alignSelf: 'center',
    fontSize: 20,
    color: 'rgba(0, 0, 0, 0.54)'
  },
  featureCountChip: {
    '&& .MuiChip-label': {
      paddingInline: 4 // force reduce inline-padding from 8px to 4px
    },
    alignSelf: 'center',
    border: 'solid 1px #918d8d',
    borderRadius: 4,
    color: '#303030',
    cursor: 'pointer',
    fontSize: 10,
    maxHeight: 18,
    minWidth: 15 // this makes the chips with […] the same width as a single digit value
  },
  headerIcon: {
    '& *': {
      maxWidth: 15,
      maxHeight: 15
    },
    color: ICON_COLOR_ENABLED
  },
  panel: {
    borderBottom: '1px solid #dadada'

  },
  panelTreeView: {},
  summaryRoot: {
    alignItems: 'center',
    minHeight: ({ treeView }) => treeView ? '40px !important' : '48px !important',
    paddingLeft: 12,
    paddingRight: 12,
    '&:hover': {
      backgroundColor: '#fafafa'
    }
  },
  summaryRootExpanded: {
    alignItems: 'center',
    height: ({ treeView }) => treeView ? '40px !important' : '48px !important',
    marginBottom: '0px !important',
    marginTop: '0px !important'
  },
  summaryContentRoot: {
    columnGap: ({ treeView }) => treeView ? null : 5,
    marginTop: 0,
    marginBottom: 0,
    paddingBottom: 5,
    paddingTop: 5
  },
  summary: { },
  details: {
    display: 'block',
    padding: 0,
    overflow: 'hidden'
  },
  detailsTreeView: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    paddingLeft: 28,
    overflow: 'hidden'
  },
  detailsPadding: {},
  upgradeWrapper: {
    width: '100%',
    display: 'inline-flex',
    justifyContent: 'flex-end'
  },
  upgradeChip: {
    marginLeft: 20,
    color: ICON_COLOR_DISABLED,
    cursor: 'pointer'
  },
  lockIconRoot: {
    opacity: 0.54,
    maxWidth: 16
  },
  badge: {
    fontSize: '0.55rem',
    height: 14,
    minWidth: 14,
    top: 5,
    right: -2,
    backgroundColor: 'rgba(158, 158, 158, 0.6)'
  }
})

class AccordionWrapper_ extends React.Component {
  onChange = (event, expanded) => {
    const { onChange: onChangeCallback } = this.props

    onChangeCallback(event)

    const isPropagationStopped = event.isPropagationStopped()

    // console.debug('onChange evt', isPropagationStopped, {...event})

    // Do not proceed if the onChange function prevented the event from propagating
    if (!isPropagationStopped) {
      this.onToggle(expanded)
    }
  }

  onToggle (expanded) {
    const { title, titleid } = this.props

    // console.debug('Expansion toggle', expanded, titleid)

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: title || 'unknown',
      action: 'expansion panel toggle ' + (expanded ? 'expand' : 'collapse')
    })
  }

  getExpansionPanel () {
    const {
      bExcludeExpandIcon = false,
      bLockIcon,
      children,
      classes,
      details,
      expanded,
      featureCount,
      headerActionItems,
      headerIcon,
      isTitleBold,
      licensed = true,
      printUIVisible,
      titleClass,
      title,
      titleid,
      tooltip,
      treeView = false
    } = this.props

    let expandIcon = null
    if (!treeView && !bExcludeExpandIcon) {
      expandIcon = <ExpandMoreIcon size="small" />
    }

    let caretIcon = null
    if (treeView) {
      caretIcon = expanded ? <CaretDownIcon className={classes.caretIcon} /> : <CaretRightIcon className={classes.caretIcon} />
    }

    // console.debug('AccordionWrapper - getExpansionPanel', titleid, expanded, featureCount)

    // If we use tree view, we add a caret icon, otherwise show the header icon
    // (but not if we are in print mode)
    const headerIconMarkup = (treeView && !printUIVisible) ? caretIcon : <div className={classes.headerIcon}>{headerIcon}</div>

    let titleFinalClass = ''
    const titleWeight = isTitleBold ? classes.titleBold : classes.titleNormal
    if (!isNil(licensed) && !licensed) {
      titleFinalClass = `${classes.titleNotLicensed} ${titleClass} ${titleWeight} `
    } else {
      titleFinalClass = `${classes.title} ${titleClass} ${titleWeight}`
    }

    // Only show feature count if the values is not defined and if it meets the minimum requirements
    let featureCountChip = null
    const showFeatureCount = typeof featureCount !== 'undefined'
    if (showFeatureCount) {
      const tooltip = typeof featureCount === 'number' ? 'Number of results' : 'Expand to see results'
      featureCountChip = (
        <Chip label={featureCount} className={classes.featureCountChip} size="small" variant="outlined" title={tooltip} />
      )
    }

    // Change styling if we use tree view
    const accordionClassName = treeView ? classes.panelTreeView : classes.panel
    const detailsClassName = treeView ? classes.detailsTreeView : classes.details

    const lockIcon = bLockIcon ? <LockIcon classes={{ root: classes.lockIconRoot }} /> : null

    const upgradeChip = !licensed
      ? (
      <div className={classes.upgradeWrapper}>
        <Chip label="Upgrade" className={classes.upgradeChip} size="small" />
        </div>
        )
      : null

    return (
      <Accordion
        elevation={0}
        TransitionProps={{
          timeout: 500,
          unmountOnExit: true
        }}
        square={true}
        className={accordionClassName}
        expanded={expanded}
        onChange={this.onChange}
        title={tooltip}
      >
        <AccordionSummary
          classes={{ root: classes.summaryRoot, expanded: classes.summaryRootExpanded, content: classes.summaryContentRoot }}
          className={classes.summary} expandIcon={expandIcon}>
          {headerIconMarkup}
          <Typography className={titleFinalClass}>{title}</Typography>
          {lockIcon}
          {featureCountChip}
          {upgradeChip}
          {headerActionItems}
        </AccordionSummary>
        <AccordionDetails className={detailsClassName}>
          {details}
          {children}
        </AccordionDetails>
      </Accordion>
    )
  }

  getWrapperWithSlide () {
    const { bSlideDirectionLeft } = this.props
    let slideDirection = ''
    if (!bSlideDirectionLeft) {
      slideDirection = 'left'
    } else {
      slideDirection = 'right'
    }
    return (
      <Slide direction={slideDirection} in={true} timeout={500} mountOnEnter unmountOnExit>
        {this.getExpansionPanel()}
      </Slide>
    )
  }

  render () {
    const { bSlideDisabled, bSlideDirectionLeft, classes, expanded, featureCount, rootClass, title, treeView = false } = this.props

    // console.debug('AccordionWrapper - render', title, expanded, featureCount, (!isNil(bSlideDisabled) && bSlideDisabled), isNil(bSlideDirectionLeft))

    let expansionPanel = null
    if ((!isNil(bSlideDisabled) && bSlideDisabled) || isNil(bSlideDirectionLeft)) {
      expansionPanel = this.getExpansionPanel()
    } else {
      expansionPanel = this.getWrapperWithSlide()
    }

    return (
      <div className={rootClass}>
        <div className={classes.root}>
          {expansionPanel}
        </div>
      </div>
    )
  }
}

AccordionWrapper_.propTypes = {
  bExcludeExpandIcon: PropTypes.bool,
  bLockIcon: PropTypes.bool,
  bSlideDirectionLeft: PropTypes.bool,
  bSlideDisabled: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.array, // If multiple children it is an array.
    PropTypes.object // If a single child it is an object.
  ]), // Note: Passing a raw string with cause an invalid children warning.
  classes: PropTypes.object.isRequired,
  currentTarget: PropTypes.object,
  details: PropTypes.oneOfType([
    PropTypes.object.isRequired, // If multiple children it is an array.
    PropTypes.string.isRequired // If a single child it is an object.
  ]),
  expanded: PropTypes.bool.isRequired,
  featureCount: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  headerIcon: PropTypes.object,
  headerActionItems: PropTypes.oneOfType([
    PropTypes.array, // If multiple buttons it is an array.
    PropTypes.object // If a single button it is an object.
  ]),
  id: PropTypes.string,
  isTitleBold: PropTypes.bool,
  licensed: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  printUIVisible: PropTypes.bool,
  rootClass: PropTypes.string,
  title: PropTypes.string.isRequired,
  titleClass: PropTypes.string,
  titleid: PropTypes.string,
  tooltip: PropTypes.string,
  treeView: PropTypes.bool
}

function mapStateToProps (state, ownProps) {
  const { printUIVisible } = state

  return {
    printUIVisible
  }
}

const mapDispatchToProps = dispatch => {
  return {}
}

const AccordionWrapper = (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(AccordionWrapper_)
  )
)

export default AccordionWrapper
